import React, { useState, useEffect } from 'react';

// Declaração do Meta Pixel para TypeScript
declare global {
  interface Window {
    fbq?: (
      action: 'track' | 'init' | 'trackCustom',
      eventName: string,
      params?: Record<string, unknown>
    ) => void;
  }
}
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  AutoAwesome as SparklesIcon,
  EmojiEvents as CrownIcon,
  CalendarToday as CalendarIcon,
  People as UsersIcon,
  MenuBook as BookOpenIcon,
  Inventory as PackageIcon,
  ChecklistRtl as ListChecksIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import { SafeAreaView } from '../../layout/SafeAreaView';
import { Capacitor } from '@capacitor/core';
import { iapService, IAP_PRODUCT_IDS } from '../../services/iapService';
import { recordSubscriptionTransaction, logSubscriptionEvent, redeemCoupon } from '../../services/subscriptionService';
import { TextField } from '@mui/material';
import { appleOfferCode } from '../../services/appleOfferCode';
import { stripeApi } from '../../services/stripeApiService';

const PricingTable = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { upgradeToPlan, manageSubscription, isActive, plan: currentPlan, subscription, refreshSubscription } = useSubscription();
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    plan: 'basic' | 'pro' | null;
  }>({ open: false, plan: null });

  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

  // Coupon State
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponMessage, setCouponMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [pendingPromotionCode, setPendingPromotionCode] = useState<string | null>(() => searchParams.get('coupon'));

  useEffect(() => {
    const codeFromUrl = searchParams.get('coupon');
    if (codeFromUrl) setPendingPromotionCode(codeFromUrl);
  }, [searchParams]);

  // Pré-carregar produtos no iOS (opcional, apenas para performance)
  React.useEffect(() => {
    if (Capacitor.getPlatform() === 'ios') {
      iapService.getProducts(Object.values(IAP_PRODUCT_IDS)).catch(err => {
        console.error('Erro ao pré-carregar produtos:', err);
      });
    }
  }, []);

  const handleSubscribe = (targetPlan: 'basic' | 'pro') => {
    if (!user) {
      navigate('/login');
      return;
    }

    const isTrialing = subscription?.status === 'trialing';
    const isExpired = subscription?.status === 'canceled' || subscription?.status === 'past_due';
    const hasPaidSubscription = isActive && currentPlan !== 'free' && !isTrialing && !isExpired;

    if (hasPaidSubscription) {
      if (Capacitor.getPlatform() === 'ios') {
        setError('Gerencie sua assinatura nas configurações do iPhone (Ajustes > Seu Nome > Assinaturas)');
        return;
      }

      setLoading(targetPlan);
      manageSubscription()
        .catch((err: any) => {
          setError(err.message || 'Erro ao abrir gerenciador de assinatura.');
        })
        .finally(() => {
          setLoading(null);
        });
      return;
    }

    logSubscriptionEvent('select_plan', { plan: targetPlan, billing: billingPeriod }, user?.uid);

    // iOS: vai direto para a compra sem dialog (Apple já mostra confirmação nativa)
    if (Capacitor.getPlatform() === 'ios') {
      handleConfirmSubscribe(targetPlan);
      return;
    }

    // Android/Web: mostra dialog de confirmação antes de redirecionar para Stripe
    setConfirmDialog({ open: true, plan: targetPlan });
  };


  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!user) {
      setCouponDialogOpen(false);
      navigate('/login');
      return;
    }

    setCouponLoading(true);
    setCouponMessage(null);

    try {
      const result = await redeemCoupon(user.uid, couponCode);
      if (result.success) {
        setCouponMessage({ type: 'success', text: result.message });
        await refreshSubscription();
        setTimeout(() => {
          setCouponDialogOpen(false);
          setCouponCode('');
          setCouponMessage(null);
          navigate('/checkout/success?session_id=internal_coupon');
        }, 1500);
        return;
      }

      // Em iOS, se não for cupom interno, tenta resgatar como Apple Offer Code
      if (appleOfferCode.isSupported()) {
        try {
          await appleOfferCode.present();
          setCouponDialogOpen(false);
          setCouponCode('');
          setCouponMessage(null);
          return;
        } catch {
          // se falhar, mantém a mensagem do redeemCoupon
        }
      }

      // Web/Android: valida como Stripe promotion code e pré-aplica no checkout
      const stripeCode = await stripeApi.findActivePromotionCodeByName(couponCode.trim().toUpperCase());
      if (stripeCode) {
        setPendingPromotionCode(stripeCode.code);
        setCouponMessage({ type: 'success', text: `Cupom ${stripeCode.code} aplicado. Escolha um plano para finalizar.` });
        setTimeout(() => {
          setCouponDialogOpen(false);
          setCouponMessage(null);
        }, 1500);
        return;
      }

      setCouponMessage({ type: 'error', text: result.message });
    } catch {
      setCouponMessage({ type: 'error', text: 'Erro ao processar cupom.' });
    } finally {
      setCouponLoading(false);
    }
  };

  const openCouponFlow = async () => {
    if (appleOfferCode.isSupported()) {
      try {
        await appleOfferCode.present();
        return;
      } catch {
        // fallback para dialog
      }
    }
    setCouponDialogOpen(true);
  };

  const handleConfirmSubscribe = async (directPlan?: 'basic' | 'pro') => {
    const targetPlan = directPlan || confirmDialog.plan;
    if (!targetPlan) return;

    // Log checkout start
    logSubscriptionEvent('checkout_start', {
      plan: targetPlan,
      period: billingPeriod
    }, user?.uid);

    setConfirmDialog({ open: false, plan: null });
    setLoading(targetPlan);
    setError(null);

    try {
      if (Capacitor.getPlatform() === 'ios') {
        let productId = '';
        if (targetPlan === 'basic') {
          productId = billingPeriod === 'monthly' ? IAP_PRODUCT_IDS.basic_monthly : IAP_PRODUCT_IDS.basic_yearly;
        } else {
          productId = billingPeriod === 'monthly' ? IAP_PRODUCT_IDS.pro_monthly : IAP_PRODUCT_IDS.pro_yearly;
        }

        try {
          const purchase = await iapService.purchase(productId);

          if (!purchase?.transactionId) {
            throw new Error('Compra não completada. Tente novamente.');
          }

          await iapService.validateAndActivate(user!.uid, purchase.transactionId, purchase.productId || productId);
          await refreshSubscription();

          // Calcular valor da assinatura (usado para Firestore e Meta Pixel)
          const isMonthly = billingPeriod === 'monthly';
          const purchaseValue = targetPlan === 'basic'
            ? (isMonthly ? 19.90 : 199.90)
            : (isMonthly ? 25.90 : 259.90);

          // Registrar venda no Firestore
          try {
            recordSubscriptionTransaction({
              userId: user!.uid,
              email: user!.email || 'N/A',
              userName: user!.displayName || 'N/A',
              plan: targetPlan,
              period: billingPeriod,
              value: purchaseValue,
              provider: 'apple',
              platform: 'ios',
              deviceModel: window.navigator.userAgent || 'iOS Device',
              osVersion: 'iOS',
              status: 'active',
              transactionId: purchase.transactionId
            }).catch(e => console.error('Erro silent stats:', e));
          } catch (logError) {
            console.error('Erro ao registrar estatísticas de venda', logError);
          }

          // Meta Pixel - Rastrear compra para tráfego pago (iOS)
          try {
            if (typeof window.fbq !== 'undefined') {
              window.fbq('track', 'Purchase', {
                value: purchaseValue,
                currency: 'BRL',
                content_type: 'subscription',
                content_name: `${targetPlan}_${billingPeriod}`,
              });
              window.fbq('track', 'Subscribe', {
                value: purchaseValue,
                currency: 'BRL',
                predicted_ltv: billingPeriod === 'annual' ? purchaseValue : purchaseValue * 12,
              });
              console.log('[Meta Pixel] Purchase event tracked:', { value: purchaseValue, plan: targetPlan, period: billingPeriod });
            }
          } catch (fbError) {
            console.error('[Meta Pixel] Erro ao rastrear Purchase:', fbError);
          }

          setLoading(null);
          setError('Assinatura ativada com sucesso! Redirecionando...');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        } catch (iapError: any) {
          // Se o erro é de produto não encontrado, mostra instrução específica
          if (iapError.message?.includes('não encontrado') || iapError.message?.includes('not found')) {
            throw new Error('Os produtos de assinatura ainda não estão disponíveis. Certifique-se de que há uma build aprovada no TestFlight.');
          }
          throw iapError;
        }
      }

      // Android/Web: Usar Stripe
      const planKey = targetPlan === 'basic' ? 'premium' : 'pro';

      // Meta Pixel - Rastrear início de checkout para tráfego pago (Web/Android)
      try {
        const isMonthly = billingPeriod === 'monthly';
        const checkoutValue = targetPlan === 'basic'
          ? (isMonthly ? 19.90 : 199.90)
          : (isMonthly ? 25.90 : 259.90);

        if (typeof window.fbq !== 'undefined') {
          window.fbq('track', 'InitiateCheckout', {
            value: checkoutValue,
            currency: 'BRL',
            content_type: 'subscription',
            content_name: `${targetPlan}_${billingPeriod}`,
          });
          console.log('[Meta Pixel] InitiateCheckout event tracked:', { value: checkoutValue, plan: targetPlan, period: billingPeriod });
        }
      } catch (fbError) {
        console.error('[Meta Pixel] Erro ao rastrear InitiateCheckout:', fbError);
      }

      await upgradeToPlan(planKey, billingPeriod, pendingPromotionCode || undefined);
    } catch (err: any) {
      setError(err.message || 'Erro ao processar assinatura. Tente novamente.');
      setLoading(null);
    }
  };

  const handleCancelSubscribe = () => {
    setConfirmDialog({ open: false, plan: null });
  };

  const getButtonText = (cardPlan: 'basic' | 'pro') => {
    if (loading === cardPlan) return <CircularProgress size={24} sx={{ color: 'white' }} />;

    const mappedCurrentPlan = currentPlan === 'premium' ? 'basic' : currentPlan;
    const isExpired = subscription?.status === 'canceled' || subscription?.status === 'past_due';
    const hasActiveSubscription = isActive && currentPlan !== 'free' && !isExpired;

    if (hasActiveSubscription && mappedCurrentPlan === cardPlan) {
      return 'Gerenciar Assinatura';
    }

    if (hasActiveSubscription) {
      return 'Alterar Plano (Gerenciar)';
    }

    return `Assinar ${cardPlan.charAt(0).toUpperCase() + cardPlan.slice(1)}`;
  };

  const features = [
    {
      category: "Eventos",
      icon: CalendarIcon,
      items: [
        { name: "Eventos por mês", basic: "10", pro: "Ilimitado" },
        { name: "Histórico de eventos", basic: "Ilimitado", pro: "Ilimitado" },
        { name: "Upload de fotos/vídeos", basic: "50 fotos + vídeos", pro: "Ilimitado" },
      ]
    },
    {
      category: "Gestão de Convidados",
      icon: UsersIcon,
      items: [
        { name: "Convidados cadastrados", basic: "50", pro: "Ilimitado" },
        { name: "Grupos de convidados", basic: "10", pro: "Ilimitado" },
        { name: "Envio de convites", basic: true, pro: true },
      ]
    },
    {
      category: "Receitas",
      icon: BookOpenIcon,
      items: [
        { name: "Receitas no caderno", basic: "30", pro: "Ilimitado" },
        { name: "Upload de vídeos", basic: false, pro: true },
        { name: "Calculadora de porções", basic: true, pro: true },
      ]
    },
    {
      category: "Acervo",
      icon: PackageIcon,
      items: [
        { name: "Itens cadastrados", basic: "100", pro: "Ilimitado" },
        { name: "Categorias personalizadas", basic: "15", pro: "Ilimitado" },
        { name: "Histórico de uso", basic: true, pro: true },
        { name: "Compartilhar acervo", basic: true, pro: true },
      ]
    },
    {
      category: "Organização",
      icon: ListChecksIcon,
      items: [
        { name: "Tarefas (To-Do List)", basic: "Ilimitado", pro: "Ilimitado" },
        { name: "Listas de compras", basic: "5 ativas", pro: "Ilimitado" },
        { name: "Wishlist", basic: "30 itens", pro: "Ilimitado" },
      ]
    },
    {
      category: "Recursos Premium",
      icon: ShieldIcon,
      items: [
        { name: "Suporte prioritário", basic: false, pro: true },
      ]
    },
  ];

  const renderValue = (value: string | boolean) => {
    if (value === true) return <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />;
    if (value === false) return <CloseIcon sx={{ color: '#E0E0E0', fontSize: 20 }} />;
    return <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>{value}</Typography>;
  };

  // Mobile View - Card Layout
  if (isMobile) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        pb: 2,
        px: 2,
      }}>
        <Container maxWidth="sm">
          {/* Header com logo e navegação */}
          <Box sx={{
            position: 'relative',
            backgroundColor: theme.colors.primary,
            borderRadius: theme.radii.large,
            mb: 3,
            overflow: 'hidden',
          }}>
            {/* Flores decorativas */}
            <img
              src="/hostly-02.png"
              alt="Logo decorativa"
              style={{
                position: "absolute",
                top: -15,
                left: -40,
                width: 120,
                opacity: 0.3,
                transform: "rotate(15deg)"
              }}
            />

            <img
              src="/hostly-02.png"
              alt="Logo decorativa"
              style={{
                position: "absolute",
                bottom: -10,
                right: -40,
                width: 120,
                opacity: 0.2
              }}
            />

            <Box sx={{
              position: 'relative',
              zIndex: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>

              {/* Logo central */}
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img
                  src="/hostly_Prancheta 1.png"
                  alt="Hostly Logo"
                  style={{
                    height: "60px",
                    width: "auto",
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Título */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.colors.text.primary, mb: 1 }}>
              Escolha seu Plano
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 1 }}>
              Organize eventos incríveis
            </Typography>

            <Button
              color="primary"
              onClick={openCouponFlow}
              sx={{ mb: 2, textTransform: 'none', textDecoration: 'underline', fontSize: '0.8rem' }}
            >
              Possui um código promocional?
            </Button>

            {pendingPromotionCode && (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: theme.colors.primary + '22', borderRadius: theme.radii.medium, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                  Cupom {pendingPromotionCode} aplicado
                </Typography>
                <Button size="small" onClick={() => setPendingPromotionCode(null)} sx={{ textTransform: 'none', minWidth: 0, fontSize: '0.75rem' }}>
                  Remover
                </Button>
              </Box>
            )}

            {/* Toggle de período */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: theme.colors.card,
              borderRadius: theme.radii.pill,
              padding: 0.5,
              maxWidth: 300,
              margin: '0 auto',
              border: `1px solid ${theme.colors.border}`,
            }}>
              <Button
                onClick={() => setBillingPeriod('monthly')}
                sx={{
                  flex: 1,
                  backgroundColor: billingPeriod === 'monthly' ? theme.colors.primary : 'transparent',
                  color: billingPeriod === 'monthly' ? 'white' : theme.colors.text.primary,
                  borderRadius: theme.radii.pill,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  py: 0.75,
                  '&:hover': {
                    backgroundColor: billingPeriod === 'monthly' ? theme.colors.primary : theme.colors.card,
                  },
                }}
              >
                Mensal
              </Button>
              <Button
                onClick={() => setBillingPeriod('annual')}
                sx={{
                  flex: 1,
                  backgroundColor: billingPeriod === 'annual' ? theme.colors.primary : 'transparent',
                  color: billingPeriod === 'annual' ? 'white' : theme.colors.text.primary,
                  borderRadius: theme.radii.pill,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  py: 0.75,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: billingPeriod === 'annual' ? theme.colors.primary : theme.colors.card,
                  },
                }}
              >
                Anual
                <Chip
                  label="ECONOMIA"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -4,
                    fontSize: '0.5rem',
                    height: 16,
                    backgroundColor: theme.colors.secondary,
                    color: 'white'
                  }}
                />
              </Button>
            </Box>
          </Box>

          {/* Error Dialog */}
          <Dialog
            open={!!error}
            onClose={() => setError(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{error?.includes('sucesso') ? 'Sucesso!' : 'Atenção'}</DialogTitle>
            <DialogContent>
              <Typography>{error}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setError(null)} variant="contained">
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* FREE Accordion */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '24px',
              mb: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.card,
            }}
          >
            <Box
              onClick={() => setExpandedAccordion(expandedAccordion === 'free' ? false : 'free')}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: expandedAccordion === 'free' ? 'rgba(0,0,0,0.02)' : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SparklesIcon sx={{ fontSize: 24, color: theme.colors.text.secondary }} />
                  <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.colors.text.primary, textTransform: 'uppercase' }}>
                    Free
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text.primary }}>
                    Grátis
                  </Typography>
                  <ExpandMoreIcon
                    sx={{
                      color: theme.colors.text.secondary,
                      transform: expandedAccordion === 'free' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {expandedAccordion === 'free' && (
              <Box sx={{ p: 3, pt: 0 }}>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: theme.colors.text.primary, mb: 0.5 }}>
                    R$ 0
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                    Grátis para sempre
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Reuse logic would be complex, copying structure for clarity/speed as per LandingPage */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarIcon sx={{ fontSize: 18, color: theme.colors.secondary }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Eventos</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">1 evento por mês</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">5 fotos por evento</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <UsersIcon sx={{ fontSize: 18, color: theme.colors.secondary }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Gestão de Convidados</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">5 convidados</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">2 grupos</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BookOpenIcon sx={{ fontSize: 18, color: theme.colors.secondary }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Receitas</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">5 receitas</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PackageIcon sx={{ fontSize: 18, color: theme.colors.secondary }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Acervo</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">30 itens</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">3 categorias personalizadas</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ListChecksIcon sx={{ fontSize: 18, color: theme.colors.secondary }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Organização</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CheckIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                      <Typography variant="body2">Tarefas ilimitadas</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CloseIcon sx={{ fontSize: 14, color: '#E0E0E0' }} />
                      <Typography variant="body2">Listas de compras</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                      <CloseIcon sx={{ fontSize: 14, color: '#E0E0E0' }} />
                      <Typography variant="body2">Wishlist</Typography>
                    </Box>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  disabled={currentPlan === 'free'}
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    mt: 3,
                    color: theme.colors.text.primary,
                    borderColor: theme.colors.border,
                    fontWeight: 700,
                    py: 1.5,
                    borderRadius: theme.radii.medium,
                    '&:hover': {
                      borderColor: theme.colors.text.primary,
                      backgroundColor: 'rgba(0,0,0,0.05)'
                    }
                  }}
                >
                  {currentPlan === 'free' ? 'Seu Plano Atual' : 'Voltar para Basic'}
                </Button>
              </Box>
            )}
          </Paper>

          {/* BASIC Accordion */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '24px',
              mb: 2,
              overflow: 'hidden',
              border: `2px solid ${theme.colors.secondary}`,
              backgroundColor: theme.colors.card,
            }}
          >
            <Box
              onClick={() => setExpandedAccordion(expandedAccordion === 'basic' ? false : 'basic')}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: expandedAccordion === 'basic' ? 'rgba(0,0,0,0.02)' : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SparklesIcon sx={{ fontSize: 24, color: theme.colors.secondary }} />
                  <Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.colors.text.primary, textTransform: 'uppercase' }}>
                      Basic
                    </Typography>
                    {billingPeriod === 'annual' && (
                      <Typography sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: theme.colors.text.secondary,
                        textDecoration: 'line-through',
                      }}>
                        DE: R$ 238
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: theme.colors.secondary }}>
                    {billingPeriod === 'monthly' ? 'R$ 19,90' : 'R$ 199,90'}
                  </Typography>
                  <ExpandMoreIcon
                    sx={{
                      color: theme.colors.secondary,
                      transform: expandedAccordion === 'basic' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {expandedAccordion === 'basic' && (
              <Box sx={{ p: 3, pt: 0 }}>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Features */}
                  {features.map((section, idx) => (
                    <Box key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <section.icon sx={{ fontSize: 18, color: theme.colors.secondary }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>{section.category}</Typography>
                      </Box>
                      {section.items.map((feature, fIdx) => (
                        <Box
                          key={fIdx}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            ml: 3,
                            mb: 0.5
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {feature.name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {renderValue(feature.basic)}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleSubscribe('basic')}
                  disabled={loading !== null}
                  sx={{
                    backgroundColor: theme.colors.secondary,
                    color: 'white',
                    fontWeight: 700,
                    borderRadius: theme.radii.medium,
                    py: 1.5,
                    mt: 3,
                  }}
                >
                  {getButtonText('basic')}
                </Button>
              </Box>
            )}
          </Paper>

          {/* PRO Accordion */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '24px',
              mb: 2,
              overflow: 'hidden',
              border: '2px solid #F59E0B',
              backgroundColor: theme.colors.card,
            }}
          >
            <Box
              onClick={() => setExpandedAccordion(expandedAccordion === 'pro' ? false : 'pro')}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: expandedAccordion === 'pro' ? 'rgba(0,0,0,0.02)' : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CrownIcon sx={{ fontSize: 24, color: '#F59E0B' }} />
                  <Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.colors.text.primary, textTransform: 'uppercase' }}>
                      Pro
                    </Typography>
                    {billingPeriod === 'annual' && (
                      <Typography sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: theme.colors.text.secondary,
                        textDecoration: 'line-through',
                      }}>
                        DE: R$ 310
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#F59E0B' }}>
                    {billingPeriod === 'monthly' ? 'R$ 25,90' : 'R$ 259,90'}
                  </Typography>
                  <ExpandMoreIcon
                    sx={{
                      color: '#F59E0B',
                      transform: expandedAccordion === 'pro' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {expandedAccordion === 'pro' && (
              <Box sx={{ p: 3, pt: 0 }}>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Features */}
                  {features.map((section, idx) => (
                    <Box key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <section.icon sx={{ fontSize: 18, color: '#F59E0B' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>{section.category}</Typography>
                      </Box>
                      {section.items.map((feature, fIdx) => (
                        <Box
                          key={fIdx}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            ml: 3,
                            mb: 0.5
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {feature.name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {renderValue(feature.pro)}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleSubscribe('pro')}
                  disabled={loading !== null}
                  sx={{
                    backgroundColor: '#F59E0B',
                    color: 'white',
                    fontWeight: 700,
                    borderRadius: theme.radii.medium,
                    py: 1.5,
                    mt: 3,
                  }}
                >
                  {getButtonText('pro')}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Footer */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 3,
            alignItems: 'center',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SparklesIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                Cancele a qualquer momento
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                Pagamento processado via Stripe
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                7 dias de garantia
              </Typography>
            </Box>

            {/* Links Legais (Required by Apple) */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexDirection: 'column', width: '100%', maxWidth: 200 }}>
              <Button
                variant="text"
                onClick={() => navigate('/privacy-policy')}
                sx={{
                  color: theme.colors.text.secondary,
                  textDecoration: 'underline',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  py: 0.5,
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Política de Privacidade
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/terms-of-use')}
                sx={{
                  color: theme.colors.text.secondary,
                  textDecoration: 'underline',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  py: 0.5,
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Termos de Uso (EULA)
              </Button>
            </Box>

            {/* Subscription Disclaimer (Apple Requirement) */}
            <Typography variant="caption" sx={{ textAlign: 'center', color: theme.colors.text.secondary, opacity: 0.7, fontSize: '0.7rem', lineHeight: 1.4, mt: 1 }}>
              A assinatura é renovada automaticamente e pode ser cancelada a qualquer momento.
              O pagamento será cobrado na sua conta na confirmação da compra. Você pode gerenciar e cancelar suas assinaturas nas configurações da sua conta após a compra.
            </Typography>
          </Box>
        </Container>

        {/* Dialog de Confirmação */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCancelSubscribe}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Redirecionamento Externo
          </DialogTitle>

          <DialogContent>
            <Typography variant="body2">
              Você será redirecionado para nosso processador de pagamentos seguro (Stripe) para concluir a assinatura.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              • Pagamento processado com segurança pela Stripe
              <br />
              • Você pode cancelar a qualquer momento
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCancelSubscribe}>
              Cancelar
            </Button>
            <Button onClick={() => handleConfirmSubscribe()} variant="contained">
              Continuar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Coupon Redemption Dialog */}
        <Dialog open={couponDialogOpen} onClose={() => setCouponDialogOpen(false)}>
          <DialogTitle>Resgatar Cupom</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Insira seu código abaixo para ativar benefícios exclusivos.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Código do Cupom"
              fullWidth
              variant="outlined"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              disabled={couponLoading}
            />
            {couponMessage && (
              <Alert severity={couponMessage.type} sx={{ mt: 2 }}>
                {couponMessage.text}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCouponDialogOpen(false)} disabled={couponLoading}>
              Cancelar
            </Button>
            <Button onClick={handleRedeemCoupon} variant="contained" disabled={couponLoading || !couponCode.trim()}>
              {couponLoading ? <CircularProgress size={24} /> : 'Resgatar'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    );
  }

  // Desktop View - Table Layout
  return (
    <SafeAreaView>
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        pb: 4,
        px: 2,
      }}>
        <Container maxWidth="lg">
          {/* Header com logo e navegação */}
          <Box sx={{
            position: 'relative',
            backgroundColor: theme.colors.primary,
            borderRadius: theme.radii.large,
            mb: 4,
            overflow: 'hidden',
          }}>
            {/* Flores decorativas */}
            <img
              src="/hostly-02.png"
              alt="Logo decorativa"
              style={{
                position: "absolute",
                top: -25,
                left: -70,
                width: 200,
                opacity: 0.3,
                transform: "rotate(15deg)"
              }}
            />

            <img
              src="/hostly-02.png"
              alt="Logo decorativa"
              style={{
                position: "absolute",
                bottom: -15,
                right: -70,
                width: 200,
                opacity: 0.2
              }}
            />

            <Box sx={{
              position: 'relative',
              zIndex: 2,
              p: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ArrowBackIcon />
                <Typography variant="body2" sx={{ ml: 1, color: 'white' }}>
                  Voltar
                </Typography>
              </IconButton>

              {/* Logo central */}
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img
                  src="/hostly_Prancheta 1.png"
                  alt="Hostly Logo"
                  style={{
                    height: "80px",
                    width: "auto",
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Título */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: theme.colors.text.primary, mb: 1 }}>
              Escolha seu Plano Hostly
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text.secondary, mb: 1 }}>
              Organize eventos incríveis com o plano perfeito para você
            </Typography>

            <Button
              color="primary"
              onClick={openCouponFlow}
              sx={{ mb: 3, textTransform: 'none', textDecoration: 'underline' }}
            >
              Possui um código promocional ou cupom?
            </Button>

            {pendingPromotionCode && (
              <Box sx={{ mb: 3, mx: 'auto', maxWidth: 400, p: 1.5, backgroundColor: theme.colors.primary + '22', borderRadius: theme.radii.medium, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                  Cupom {pendingPromotionCode} aplicado
                </Typography>
                <Button size="small" onClick={() => setPendingPromotionCode(null)} sx={{ textTransform: 'none', minWidth: 0 }}>
                  Remover
                </Button>
              </Box>
            )}

            {/* Toggle de período */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: theme.colors.card,
              borderRadius: theme.radii.pill,
              padding: 0.5,
              maxWidth: 320,
              margin: '0 auto',
              border: `1px solid ${theme.colors.border}`,
            }}>
              <Button
                onClick={() => setBillingPeriod('monthly')}
                sx={{
                  flex: 1,
                  backgroundColor: billingPeriod === 'monthly' ? theme.colors.primary : 'transparent',
                  color: billingPeriod === 'monthly' ? 'white' : theme.colors.text.primary,
                  borderRadius: theme.radii.pill,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  py: 1,
                  '&:hover': {
                    backgroundColor: billingPeriod === 'monthly' ? theme.colors.primary : theme.colors.card,
                  },
                }}
              >
                Mensal
              </Button>
              <Button
                onClick={() => setBillingPeriod('annual')}
                sx={{
                  flex: 1,
                  backgroundColor: billingPeriod === 'annual' ? theme.colors.primary : 'transparent',
                  color: billingPeriod === 'annual' ? 'white' : theme.colors.text.primary,
                  borderRadius: theme.radii.pill,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  py: 1,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: billingPeriod === 'annual' ? theme.colors.primary : theme.colors.card,
                  },
                }}
              >
                Anual
                <Chip
                  label="ECONOMIA"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -6,
                    fontSize: '0.6rem',
                    height: 18,
                    backgroundColor: theme.colors.secondary,
                    color: 'white'
                  }}
                />
              </Button>
            </Box>
          </Box>

          {/* Erro */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Tabela de Preços */}
          <Paper
            elevation={3}
            sx={{
              borderRadius: theme.radii.large,
              overflow: 'hidden',
              backgroundColor: theme.colors.background,
            }}
          >
            <TableContainer>
              <Table>
                {/* Header */}
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.colors.card }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: theme.colors.text.primary }}>
                      Recursos
                    </TableCell>

                    {/* Basic Plan */}
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: theme.colors.card,
                        border: `2px solid ${theme.colors.secondary}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <SparklesIcon sx={{ color: theme.colors.secondary, fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>Basic {billingPeriod === 'monthly' ? 'Mensal' : 'Anual'}</Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: theme.colors.text.primary }}>
                        R$ {billingPeriod === 'monthly' ? '19,90' : '199,90'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, mb: 1, display: 'block' }}>
                        por {billingPeriod === 'monthly' ? 'mês' : 'ano'}
                      </Typography>
                      <Chip
                        label="POPULAR"
                        size="small"
                        sx={{
                          backgroundColor: theme.colors.secondary,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                        }}
                      />
                    </TableCell>

                    {/* Pro Plan */}
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: theme.colors.card,
                        border: '2px solid #F59E0B',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <CrownIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>Pro {billingPeriod === 'monthly' ? 'Mensal' : 'Anual'}</Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: theme.colors.text.primary }}>
                        R$ {billingPeriod === 'monthly' ? '25,90' : '259,90'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, mb: 1, display: 'block' }}>
                        por {billingPeriod === 'monthly' ? 'mês' : 'ano'}
                      </Typography>
                      <Chip
                        label="COMPLETO"
                        size="small"
                        sx={{
                          backgroundColor: '#F59E0B',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Features Body */}
                <TableBody>
                  {features.map((section, idx) => (
                    <React.Fragment key={idx}>
                      {/* Category Header */}
                      <TableRow sx={{ backgroundColor: theme.colors.card }}>
                        <TableCell colSpan={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <section.icon sx={{ fontSize: 18, color: theme.colors.text.secondary }} />
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                color: theme.colors.text.primary,
                              }}
                            >
                              {section.category}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Feature Items */}
                      {section.items.map((feature, fIdx) => (
                        <TableRow
                          key={fIdx}
                          sx={{
                            backgroundColor: fIdx % 2 === 0 ? theme.colors.card : theme.colors.background,
                            '&:hover': {
                              backgroundColor: theme.colors.border,
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500, color: theme.colors.text.primary }}>
                            {feature.name}
                          </TableCell>
                          <TableCell align="center" sx={{ backgroundColor: 'transparent' }}>
                            {renderValue(feature.basic)}
                          </TableCell>
                          <TableCell align="center" sx={{ backgroundColor: 'transparent' }}>
                            {renderValue(feature.pro)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* CTA Buttons */}
                  <TableRow sx={{ backgroundColor: theme.colors.card }}>
                    <TableCell />
                    <TableCell align="center" sx={{ py: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleSubscribe('basic')}
                        disabled={loading !== null}
                        sx={{
                          backgroundColor: theme.colors.secondary,
                          color: 'white',
                          fontWeight: 700,
                          borderRadius: theme.radii.medium,
                          boxShadow: 3,
                          '&:hover': {
                            backgroundColor: theme.colors.secondary,
                            opacity: 0.9,
                          },
                        }}
                      >
                        {getButtonText('basic')}
                      </Button>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleSubscribe('pro')}
                        disabled={loading !== null}
                        sx={{
                          backgroundColor: '#F59E0B',
                          color: 'white',
                          fontWeight: 700,
                          borderRadius: theme.radii.medium,
                          boxShadow: 3,
                          '&:hover': {
                            backgroundColor: '#D97706',
                          },
                        }}
                      >
                        {getButtonText('pro')}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Footer */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            mt: 4,
            flexWrap: 'wrap',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SparklesIcon sx={{ fontSize: 18, color: theme.colors.text.secondary }} />
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                Cancele a qualquer momento
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon sx={{ fontSize: 18, color: theme.colors.text.secondary }} />
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                {Capacitor.getPlatform() === 'ios'
                  ? 'Pagamento processado via Apple'
                  : 'Pagamento processado via Stripe'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon sx={{ fontSize: 18, color: theme.colors.text.secondary }} />
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                7 dias de garantia
              </Typography>
            </Box>
          </Box>

          {/* Links Legais (Required by Apple) - Desktop */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2 }}>
            <Button
              variant="text"
              onClick={() => navigate('/privacy-policy')}
              sx={{
                color: theme.colors.text.secondary,
                textDecoration: 'underline',
                textTransform: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Política de Privacidade
            </Button>
            <Button
              variant="text"
              onClick={() => navigate('/terms-of-use')}
              sx={{
                color: theme.colors.text.secondary,
                textDecoration: 'underline',
                textTransform: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Termos de Uso (EULA)
            </Button>
          </Box>

          {/* Subscription Disclaimer (Apple Requirement) - Desktop */}
          <Box sx={{ mt: 3, maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: theme.colors.text.secondary, opacity: 0.7, fontSize: '0.75rem', lineHeight: 1.5 }}>
              A assinatura é renovada automaticamente e pode ser cancelada a qualquer momento.
              O pagamento será cobrado na sua conta na confirmação da compra. Você pode gerenciar e cancelar suas assinaturas nas configurações da sua conta após a compra.
            </Typography>
          </Box>
        </Container>

        {/* Dialog de Confirmação */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCancelSubscribe}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {Capacitor.getPlatform() === 'ios' ? 'Confirmar Assinatura' : 'Redirecionamento Externo'}
          </DialogTitle>

          <DialogContent>
            {Capacitor.getPlatform() === 'ios' ? (
              <>
                <Typography variant="body2">
                  Você será direcionado para concluir a assinatura através da App Store.
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  • Pagamento processado com segurança pela Apple
                  <br />
                  • Gerencie sua assinatura nas configurações do iPhone
                  <br />
                  • Você pode cancelar a qualquer momento
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Você será redirecionado para nosso processador de pagamentos seguro (Stripe) para concluir a assinatura.
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  • Pagamento processado com segurança pela Stripe
                  <br />
                  • Você pode cancelar a qualquer momento
                </Typography>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCancelSubscribe}>
              Cancelar
            </Button>
            <Button onClick={() => handleConfirmSubscribe()} variant="contained">
              Continuar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Coupon Redemption Dialog (Desktop) */}
        <Dialog open={couponDialogOpen} onClose={() => setCouponDialogOpen(false)}>
          <DialogTitle>Resgatar Cupom</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Insira seu código abaixo para ativar benefícios exclusivos.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Código do Cupom"
              fullWidth
              variant="outlined"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              disabled={couponLoading}
            />
            {couponMessage && (
              <Alert severity={couponMessage.type} sx={{ mt: 2 }}>
                {couponMessage.text}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCouponDialogOpen(false)} disabled={couponLoading}>
              Cancelar
            </Button>
            <Button onClick={handleRedeemCoupon} variant="contained" disabled={couponLoading || !couponCode.trim()}>
              {couponLoading ? <CircularProgress size={24} /> : 'Resgatar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SafeAreaView>
  );
};

export default PricingTable;